import { useCallback, useEffect, useRef, useState } from 'react';
import { success, error } from '../pages/inc/Toast';

const PAGE_SIZE = 8;
const SEARCH_DEBOUNCE_MS = 400;
const EMPTY_PAGE = { content: [], totalPages: 0, totalElements: 0 };

// Workflow vérification / envoi aux abonnés (même logique que les communiqués).
// api : { verify(id), send(id), getAbonnees(id, page, size, search) } du module request de l'entité.
export function useEnvoiAbonnes(api) {
  const [abonnees, setAbonnees] = useState(EMPTY_PAGE);
  const [currentPage, setCurrentPage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [invalidEmails, setInvalidEmails] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const searchTimeout = useRef(null);

  useEffect(() => () => clearTimeout(searchTimeout.current), []);

  const loadAbonnees = useCallback(async (id, page = 0, searchTerm = '') => {
    setCurrentPage(page);
    setIsLoading(true);
    const response = await api.getAbonnees(id, page, PAGE_SIZE, searchTerm);
    setAbonnees(response?.status === 200 ? response.data : EMPTY_PAGE);
    setIsLoading(false);
  }, [api]);

  const handleSearch = (value, id) => {
    setSearch(value);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => loadAbonnees(id, 0, value), SEARCH_DEBOUNCE_MS);
  };

  const openEnvoi = (id) => {
    setSearch('');
    setInvalidEmails([]);
    loadAbonnees(id, 0, '');
  };

  const verify = async (id) => {
    const response = await api.verify(id);
    if (response?.status === 200) success();
    else error(response?.data?.message);
    return response?.status === 200;
  };

  // Le backend marque le contenu envoyé même si des adresses invalides ont été ignorées :
  // isSent permet aux pages de rafraîchir le statut, isComplete de fermer la modale.
  const send = async (id) => {
    setIsSending(true);
    const response = await api.send(id);
    setIsSending(false);
    if (response?.status !== 200) {
      error(response?.data?.message);
      return { isSent: false, isComplete: false };
    }
    const invalids = response.data?.invalidEmails || [];
    setInvalidEmails(invalids);
    if (invalids.length === 0) success();
    return { isSent: true, isComplete: invalids.length === 0 };
  };

  return {
    abonnees: abonnees.content, totalPages: abonnees.totalPages, totalElements: abonnees.totalElements,
    currentPage, isLoading, search, invalidEmails, isSending,
    loadAbonnees, handleSearch, openEnvoi, verify, send, resetInvalidEmails: () => setInvalidEmails([]),
  };
}
